import { useState, useCallback, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Archive,
    Plus,
    Download,
    RotateCcw,
    Trash2,
    Clock,
    HardDrive,
    Cloud,
    FileArchive,
    PackageOpen,
    ShieldAlert,
    AlertTriangle,
    CheckCircle,
    XCircle,
    FileText,
    ChevronDown,
    ChevronRight,
    Info,
    Server,
    AlertCircle,
    CheckCircle2,
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
    useBackups,
    useCreateBackup,
    useDownloadBackup,
    useDeleteBackup,
    useRestoreBackup,
    useTemplates,
    useImportTemplate,
    useStorageConfig,
    useUpdateRetentionPolicy,
    useFactoryReset,
    type BackupItem,
    type TemplateItem,
} from '@/features/data-management/hooks'
import { cn } from '@/lib/utils'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { StorageUsageBar } from '@/features/data-management/components/storage-usage-bar'
const ENTITY_OPTIONS = [
    { key: 'domains', label: 'Domains' },
    { key: 'questions', label: 'Questions' },
    { key: 'maturity_levels', label: 'Maturity Levels' },
    { key: 'score_labels', label: 'Score Labels' },
    { key: 'industries', label: 'Industries' },
    { key: 'project_initiatives', label: 'Project Initiatives' },
    { key: 'organizations', label: 'Organizations' },
    { key: 'assessments', label: 'Assessments' },
    { key: 'users', label: 'Users' },
    { key: 'roles', label: 'Roles' },
] as const

const RESET_CATEGORY_OPTIONS = [
    { key: 'assessments', label: 'Assessments', description: 'Assessments, answers, scores, recommendations, and evidence files' },
    { key: 'organizations', label: 'Organizations', description: 'Organizations and membership assignments' },
    { key: 'backups', label: 'System Backups', description: 'All stored ZIP backups and metadata' },
    { key: 'questionnaire', label: 'Questionnaire', description: 'Questions and domains' },
    { key: 'maturity', label: 'Maturity Config', description: 'Maturity levels and score labels' },
    { key: 'industries', label: 'Industries', description: 'Industries and benchmarks' },
    { key: 'initiatives', label: 'Initiatives', description: 'Project initiatives' },
    { key: 'settings', label: 'App Settings', description: 'Application configuration' },
    { key: 'audit', label: 'Audit Trail', description: 'Audit log entries' },
    { key: 'identity', label: 'Users & Roles', description: 'All user accounts and role definitions' },
] as const

function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

function formatDate(iso: string): string {
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

function StorageBackendBadge({ backend }: { backend: string }) {
    if (backend === 'minio') {
        return (
            <Badge variant="outline" className="gap-1 text-xs">
                <Cloud className="h-3 w-3" /> MinIO
            </Badge>
        )
    }
    if (backend === 'azure') {
        return (
            <Badge variant="outline" className="gap-1 text-xs">
                <Cloud className="h-3 w-3" /> Azure
            </Badge>
        )
    }
    return (
        <Badge variant="secondary" className="gap-1 text-xs">
            <HardDrive className="h-3 w-3" /> Local
        </Badge>
    )
}

/* ─── Import Results Dialog (reused for templates) ─────────────────────────── */

interface EntityResult {
    created?: number
    updated?: number
    errors?: Array<{ row: number; error: string } | string>
    skipped?: boolean
    reason?: string
    error?: string
}

interface ImportResults {
    [entity: string]: EntityResult
}

function ImportResultsDialog({
    open,
    onOpenChange,
    results,
    title,
}: {
    open: boolean
    onOpenChange: (open: boolean) => void
    results: ImportResults | null
    title?: string
}) {
    const [expandedEntities, setExpandedEntities] = useState<Set<string>>(new Set())

    const toggleEntity = (entity: string) => {
        setExpandedEntities(prev => {
            const next = new Set(prev)
            if (next.has(entity)) next.delete(entity)
            else next.add(entity)
            return next
        })
    }

    if (!results) return null

    let totalCreated = 0, totalUpdated = 0, totalErrors = 0, totalSkipped = 0
    for (const r of Object.values(results)) {
        if (r.skipped) { totalSkipped++; continue }
        if (r.error) { totalErrors++; continue }
        totalCreated += r.created ?? 0
        totalUpdated += r.updated ?? 0
        totalErrors += r.errors?.length ?? 0
    }

    const errorEntities = Object.entries(results).filter(([, r]) =>
        r.error || (r.errors && r.errors.length > 0)
    )
    const hasErrors = totalErrors > 0

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        {title || 'Import Results'}
                    </DialogTitle>
                    <DialogDescription>Review the import results below.</DialogDescription>
                </DialogHeader>

                <div className="flex flex-wrap gap-2 pb-1">
                    <Badge variant="secondary" className="gap-1.5">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        {totalCreated} created
                    </Badge>
                    <Badge variant="outline" className="gap-1.5">
                        <FileText className="h-3 w-3 text-blue-500" />
                        {totalUpdated} updated
                    </Badge>
                    {totalErrors > 0 && (
                        <Badge variant="destructive" className="gap-1.5">
                            <XCircle className="h-3 w-3" />
                            {totalErrors} error{totalErrors !== 1 ? 's' : ''}
                        </Badge>
                    )}
                    {totalSkipped > 0 && (
                        <Badge variant="outline" className="gap-1.5 text-muted-foreground">
                            {totalSkipped} skipped
                        </Badge>
                    )}
                </div>

                <Tabs defaultValue="summary" className="w-full">
                    <TabsList className="w-full">
                        <TabsTrigger value="summary" className="flex-1 gap-1.5">
                            <FileText className="h-3.5 w-3.5" /> Summary
                        </TabsTrigger>
                        <TabsTrigger value="errors" className="flex-1 gap-1.5" disabled={!hasErrors}>
                            <AlertTriangle className="h-3.5 w-3.5" /> Errors
                            {hasErrors && (
                                <Badge variant="destructive" className="ml-1 h-5 px-1.5 text-[10px]">
                                    {totalErrors}
                                </Badge>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="summary" className="max-h-72 overflow-y-auto">
                        <div className="space-y-1.5 pt-1">
                            {Object.entries(results).map(([entity, result]) => (
                                <div key={entity} className="flex items-center justify-between rounded-md border px-3 py-2.5 transition-colors hover:bg-muted/30">
                                    <div className="flex items-center gap-2">
                                        {result.skipped ? (
                                            <div className="h-4 w-4 rounded-full bg-muted flex items-center justify-center">
                                                <span className="text-[10px] text-muted-foreground">—</span>
                                            </div>
                                        ) : result.error ? (
                                            <XCircle className="h-4 w-4 text-destructive" />
                                        ) : (result.errors?.length ?? 0) > 0 ? (
                                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                                        ) : (
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                        )}
                                        <span className="font-medium text-sm capitalize">{entity.replace(/_/g, ' ')}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        {result.skipped ? (
                                            <span className="text-xs text-muted-foreground">{result.reason}</span>
                                        ) : result.error ? (
                                            <Badge variant="destructive" className="text-[11px]">Failed</Badge>
                                        ) : (
                                            <>
                                                {(result.created ?? 0) > 0 && <Badge variant="secondary" className="text-[11px]">{result.created} created</Badge>}
                                                {(result.updated ?? 0) > 0 && <Badge variant="outline" className="text-[11px]">{result.updated} updated</Badge>}
                                                {(result.errors?.length ?? 0) > 0 && <Badge variant="destructive" className="text-[11px]">{result.errors!.length} error{result.errors!.length !== 1 ? 's' : ''}</Badge>}
                                                {(result.created ?? 0) === 0 && (result.updated ?? 0) === 0 && (result.errors?.length ?? 0) === 0 && (
                                                    <span className="text-xs text-muted-foreground">No changes</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="errors" className="max-h-72 overflow-y-auto">
                        <div className="space-y-2 pt-1">
                            {errorEntities.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2">
                                    <CheckCircle className="h-8 w-8 text-green-500/60" />
                                    <span className="text-sm">No errors</span>
                                </div>
                            ) : (
                                errorEntities.map(([entity, result]) => (
                                    <div key={entity} className="rounded-md border overflow-hidden">
                                        <button type="button" onClick={() => toggleEntity(entity)}
                                            className="flex items-center justify-between w-full px-3 py-2.5 text-left hover:bg-muted/30 transition-colors">
                                            <div className="flex items-center gap-2">
                                                {expandedEntities.has(entity) ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                                                <XCircle className="h-4 w-4 text-destructive" />
                                                <span className="font-medium text-sm capitalize">{entity.replace(/_/g, ' ')}</span>
                                            </div>
                                            <Badge variant="destructive" className="text-[11px]">
                                                {result.error ? 1 : result.errors?.length ?? 0} error{((result.error ? 1 : result.errors?.length ?? 0) !== 1) ? 's' : ''}
                                            </Badge>
                                        </button>
                                        {expandedEntities.has(entity) && (
                                            <div className="border-t bg-muted/20 px-3 py-2 space-y-1.5 max-h-40 overflow-y-auto">
                                                {result.error ? (
                                                    <p className="text-xs text-destructive font-mono break-all">{result.error}</p>
                                                ) : (
                                                    result.errors?.map((err, i) => {
                                                        const errMsg = typeof err === 'string' ? err : err.error
                                                        const rowIdx = typeof err === 'object' ? err.row : undefined
                                                        return (
                                                            <div key={i} className="flex gap-2 text-xs">
                                                                {rowIdx !== undefined && <span className="text-muted-foreground shrink-0">Row {rowIdx}:</span>}
                                                                <span className="text-destructive font-mono break-all">{errMsg}</span>
                                                            </div>
                                                        )
                                                    })
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </TabsContent>
                </Tabs>

                <DialogFooter>
                    <Button onClick={() => onOpenChange(false)}>Done</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

/* ─── Main Page ────────────────────────────────────────────────────────────── */

export function SettingsBackupRestorePage() {
    const { toast } = useToast()

    // Backup state
    const { data: backups, isLoading: backupsLoading } = useBackups()
    const createBackup = useCreateBackup()
    const downloadBackup = useDownloadBackup()
    const deleteBackup = useDeleteBackup()
    const restoreBackup = useRestoreBackup()

    // Templates state
    const { data: templates, isLoading: templatesLoading } = useTemplates()
    const importTemplate = useImportTemplate()

    // Storage config
    const { data: storageConfig } = useStorageConfig()
    const factoryReset = useFactoryReset()

    // Create backup form
    const [backupName, setBackupName] = useState('')
    const [backupDescription, setBackupDescription] = useState('')
    const [selectedEntities, setSelectedEntities] = useState<Set<string>>(
        new Set(ENTITY_OPTIONS.map(e => e.key))
    )
    const [isCreating, setIsCreating] = useState(false)

    // Dialogs
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
    const [restoreTarget, setRestoreTarget] = useState<BackupItem | null>(null)
    const [restoreConfirmText, setRestoreConfirmText] = useState('')
    const [restoreResultsOpen, setRestoreResultsOpen] = useState(false)
    const [restoreResults, setRestoreResults] = useState<ImportResults | null>(null)

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState<BackupItem | null>(null)

    const [templateResultsOpen, setTemplateResultsOpen] = useState(false)
    const [templateResults, setTemplateResults] = useState<ImportResults | null>(null)
    const [importingTemplate, setImportingTemplate] = useState<string | null>(null)

    // Retention policy local state
    const [retentionCount, setRetentionCount] = useState<number>(50)
    const [retentionDays, setRetentionDays] = useState<number>(90)
    const updateRetention = useUpdateRetentionPolicy()

    // Factory Reset state
    const [resetDialogOpen, setResetDialogOpen] = useState(false)
    const [resetConfirmText, setResetConfirmText] = useState('')
    const [resetCategories, setResetCategories] = useState<Set<string>>(new Set())

    // Upload-to-restore state
    const [uploadFile, setUploadFile] = useState<File | null>(null)
    const [isRestoringUpload, setIsRestoringUpload] = useState(false)

    useEffect(() => {
        if (storageConfig) {
            setRetentionCount(storageConfig.max_count)
            setRetentionDays(storageConfig.max_age_days)
        }
    }, [storageConfig])

    const allSelected = selectedEntities.size === ENTITY_OPTIONS.length

    const toggleEntity = (key: string) => {
        setSelectedEntities(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const toggleAll = () => {
        if (allSelected) {
            setSelectedEntities(new Set())
        } else {
            setSelectedEntities(new Set(ENTITY_OPTIONS.map(e => e.key)))
        }
    }

    // ── Handlers ──────────────────────────────────────────────────────

    const handleCreateBackup = async () => {
        setIsCreating(true)
        try {
            const entities = allSelected ? [] : Array.from(selectedEntities)
            await createBackup.mutateAsync({
                name: backupName.trim(),
                description: backupDescription.trim() || undefined,
                entities: entities.length > 0 ? entities : undefined,
            })
            toast({ title: 'Backup Created', description: `"${backupName}" has been saved successfully.` })
            setBackupName('')
            setBackupDescription('')
        } catch (err: any) {
            toast({
                title: 'Backup Failed',
                description: err?.response?.data?.error || 'Something went wrong.',
                variant: 'destructive',
            })
        } finally {
            setIsCreating(false)
        }
    }

    const handleDownload = useCallback(async (backup: BackupItem) => {
        try {
            await downloadBackup(backup)
            toast({ title: 'Download Started', description: `Downloading "${backup.name}"…` })
        } catch {
            toast({ title: 'Download Failed', description: 'Could not download backup.', variant: 'destructive' })
        }
    }, [downloadBackup, toast])

    const handleDelete = async () => {
        if (!deleteTarget) return
        try {
            await deleteBackup.mutateAsync(deleteTarget.id)
            toast({ title: 'Backup Deleted', description: `"${deleteTarget.name}" has been removed.` })
            setDeleteDialogOpen(false)
            setDeleteTarget(null)
        } catch (err: any) {
            toast({
                title: 'Delete Failed',
                description: err?.response?.data?.error || 'Something went wrong.',
                variant: 'destructive',
            })
        }
    }

    const handleRestore = async () => {
        if (!restoreTarget && !uploadFile) return
        
        try {
            let res;
            if (uploadFile) {
                setIsRestoringUpload(true);
                const formData = new FormData();
                formData.append('file', uploadFile);
                
                // We'll use fetch directly or axios if available. The hook 'useRestoreBackup' 
                // is likely configured for ID-based restores.
                // Assuming we need a direct API call here.
                const token = JSON.parse(localStorage.getItem('auth-storage') || '{}')?.state?.accessToken;
                const response = await fetch('/api/data-management/backups/upload-restore', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData
                });
                
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Upload restore failed');
                }
                res = { data: await response.json() };
            } else {
                res = await restoreBackup.mutateAsync(restoreTarget!.id)
            }

            const data = res?.data
            setRestoreDialogOpen(false)
            setRestoreConfirmText('')
            setRestoreTarget(null)
            setUploadFile(null)
            setRestoreResults(data?.results || {})
            setRestoreResultsOpen(true)

            if (data?.identity_restored) {
                toast({ title: 'Restore Complete', description: 'Identity was restored — you will be logged out.' })
                localStorage.removeItem('auth-storage')
                setTimeout(() => { window.location.href = '/auth/login' }, 2000)
            } else {
                toast({ title: 'Restore Complete', description: data?.message || 'Data has been restored.' })
                setTimeout(() => window.location.reload(), 2500)
            }
        } catch (err: any) {
            toast({
                title: 'Restore Failed',
                description: err.message || err?.response?.data?.error || 'Something went wrong.',
                variant: 'destructive',
            })
        } finally {
            setIsRestoringUpload(false);
        }
    }

    const handleImportTemplate = async (template: TemplateItem) => {
        setImportingTemplate(template.filename)
        try {
            const res = await importTemplate.mutateAsync(template.filename)
            const data = res?.data?.results ?? res?.data ?? {}
            setTemplateResults(data)
            setTemplateResultsOpen(true)
            toast({ title: 'Template Imported', description: `"${template.name}" imported successfully.` })
        } catch (err: any) {
            toast({
                title: 'Import Failed',
                description: err?.response?.data?.error || 'Something went wrong.',
                variant: 'destructive',
            })
        } finally {
            setImportingTemplate(null)
        }
    }

    const handleUpdateRetention = async () => {
        try {
            await updateRetention.mutateAsync({
                max_count: retentionCount,
                max_age_days: retentionDays,
            })
            toast({ title: 'Policy Updated', description: 'Backup retention settings have been saved.' })
        } catch (err: any) {
            toast({
                title: 'Update Failed',
                description: err?.response?.data?.error || 'Could not save retention policy.',
                variant: 'destructive',
            })
        }
    }

    const handleFactoryReset = async () => {
        const categories = Array.from(resetCategories)
        const includesIdentity = resetCategories.has('identity')
        try {
            const res = await factoryReset.mutateAsync(categories.length > 0 ? categories : undefined)
            const data = res?.data
            setResetDialogOpen(false)
            setResetConfirmText('')
            setResetCategories(new Set())
            toast({
                title: 'Factory Reset Complete',
                description: data?.message || 'Selected data has been deleted.',
            })

            if (includesIdentity || data?.identity_deleted) {
                // Clear auth and redirect to login
                localStorage.removeItem('auth-storage')
                setTimeout(() => { window.location.href = '/auth/login' }, 1000)
            } else {
                // Soft reload to clear cached state
                setTimeout(() => window.location.reload(), 1500)
            }
        } catch (err: any) {
            toast({
                title: 'Factory Reset Failed',
                description: err?.response?.data?.error || 'Something went wrong.',
                variant: 'destructive',
            })
        }
    }

    return (
        <div className="space-y-6">

            {/* ── Storage Info Banner ── */}
            {storageConfig && (
                <div className="space-y-4">
                    <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3">
                        <Info className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1.5">
                                <span className="font-medium text-foreground">Storage Backend:</span>
                                <StorageBackendBadge backend={storageConfig.backend} />
                                {storageConfig.path && <span className="font-mono text-[11px] opacity-70">{storageConfig.path}</span>}
                                {storageConfig.endpoint && <span className="font-mono text-[11px] opacity-70">{storageConfig.endpoint}</span>}
                                {storageConfig.container && <span className="font-mono text-[11px] opacity-70">/ {storageConfig.container}</span>}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <div className="space-y-1">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <RotateCcw className="h-4 w-4" />
                                            Retention Policy
                                        </CardTitle>
                                        <CardDescription className="text-xs">
                                            Old backups are automatically deleted to save storage space.
                                        </CardDescription>
                                    </div>
                                    <AuthorizationGate actions={['system_export']}>
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            onClick={handleUpdateRetention}
                                            disabled={updateRetention.isPending || (
                                                retentionCount === storageConfig.max_count &&
                                                retentionDays === storageConfig.max_age_days
                                            )}
                                        >
                                            {updateRetention.isPending ? 'Saving…' : 'Save Policy'}
                                        </Button>
                                    </AuthorizationGate>
                                </div>
                            </CardHeader>
                            <CardContent className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="max-count" className="text-xs flex items-center justify-between">
                                        Max Backups
                                        {storageConfig.readonly_fields.includes('max_count') && (
                                            <Badge variant="outline" className="h-4 px-1 text-[9px] uppercase opacity-50">Locked by Env</Badge>
                                        )}
                                    </Label>
                                    <Input
                                        id="max-count"
                                        type="number"
                                        value={retentionCount}
                                        onChange={(e) => setRetentionCount(parseInt(e.target.value) || 0)}
                                        disabled={storageConfig.readonly_fields.includes('max_count')}
                                        className="h-8 text-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="max-age" className="text-xs flex items-center justify-between">
                                        Retention Days
                                        {storageConfig.readonly_fields.includes('max_age_days') && (
                                            <Badge variant="outline" className="h-4 px-1 text-[9px] uppercase opacity-50">Locked by Env</Badge>
                                        )}
                                    </Label>
                                    <Input
                                        id="max-age"
                                        type="number"
                                        value={retentionDays}
                                        onChange={(e) => setRetentionDays(parseInt(e.target.value) || 0)}
                                        disabled={storageConfig.readonly_fields.includes('max_age_days')}
                                        className="h-8 text-sm"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <div className="space-y-1">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <Server className="h-4 w-4" />
                                            Storage Health
                                        </CardTitle>
                                        <CardDescription className="text-xs">
                                            Status and capacity of the connected backup storage.
                                        </CardDescription>
                                    </div>
                                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted/50">
                                        {storageConfig.status === 'connected' ? (
                                            <>
                                                <CheckCircle2 className="h-3 w-3 text-green-500" />
                                                <span className="text-[10px] font-medium uppercase text-green-600 dark:text-green-400">Connected</span>
                                            </>
                                        ) : (
                                            <>
                                                <AlertCircle className="h-3 w-3 text-destructive" />
                                                <span className="text-[10px] font-medium uppercase text-destructive">Error</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {storageConfig.status === 'error' && (
                                    <div className="rounded-md bg-destructive/10 p-3 flex items-start gap-2">
                                        <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                                        <p className="text-xs text-destructive leading-tight">{storageConfig.error_message}</p>
                                    </div>
                                )}

                                <StorageUsageBar 
                                    totalBytes={storageConfig.total_size_bytes}
                                    usedBytes={storageConfig.used_size_bytes}
                                    backupsBytes={storageConfig.backups_size_bytes}
                                    attachmentsBytes={storageConfig.attachments_size_bytes}
                                    templatesBytes={storageConfig.templates_size_bytes}
                                    className="pt-1"
                                />
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            {/* ── Create Backup ── */}
            <AuthorizationGate actions={['system_export']}>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Plus className="h-5 w-5" />
                            Create Backup
                        </CardTitle>
                        <CardDescription>
                            Take a named snapshot of the system data. Select which entities to include.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="backup-name">Backup Name *</Label>
                                <Input
                                    id="backup-name"
                                    placeholder="e.g. Pre-migration snapshot"
                                    value={backupName}
                                    onChange={(e) => setBackupName(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="backup-desc">Description</Label>
                                <Input
                                    id="backup-desc"
                                    placeholder="Optional notes about this backup"
                                    value={backupDescription}
                                    onChange={(e) => setBackupDescription(e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium">Entity Types</span>
                                <button type="button" className="text-xs text-primary hover:underline cursor-pointer" onClick={toggleAll}>
                                    {allSelected ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                                {ENTITY_OPTIONS.map(entity => (
                                    <label
                                        key={entity.key}
                                        className="flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer hover:bg-accent transition-colors"
                                    >
                                        <Checkbox
                                            checked={selectedEntities.has(entity.key)}
                                            onCheckedChange={() => toggleEntity(entity.key)}
                                        />
                                        <span className="text-sm">{entity.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-2">
                            <Button
                                onClick={handleCreateBackup}
                                disabled={selectedEntities.size === 0 || isCreating}
                            >
                                <Archive className="h-4 w-4 mr-2" />
                                {isCreating ? 'Creating…' : 'Create Backup'}
                            </Button>
                            {selectedEntities.size > 0 && (
                                <span className="text-xs text-muted-foreground">
                                    {allSelected ? 'All entities' : `${selectedEntities.size} entit${selectedEntities.size === 1 ? 'y' : 'ies'}`} selected
                                </span>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </AuthorizationGate>

            {/* ── Backups List ── */}
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <FileArchive className="h-5 w-5" />
                        Backups
                        {backups && backups.length > 0 && (
                            <Badge variant="secondary" className="ml-2">{backups.length}</Badge>
                        )}
                        <div className="ml-auto flex items-center gap-2">
                            <input
                                type="file"
                                id="backup-upload"
                                className="hidden"
                                accept=".zip"
                                onChange={(e) => {
                                    const file = e.target.files?.[0]
                                    if (file) {
                                        setUploadFile(file)
                                        setRestoreDialogOpen(true)
                                    }
                                    e.target.value = ''
                                }}
                            />
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => document.getElementById('backup-upload')?.click()}
                                className="h-8 gap-2"
                            >
                                <Download className="h-3.5 w-3.5 rotate-180" />
                                Upload & Restore
                            </Button>
                        </div>
                    </div>
                    <CardDescription>
                        System backups ordered by most recent. Download, restore, or delete backups.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {backupsLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : !backups || backups.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-3">
                            <Archive className="h-12 w-12 opacity-30" />
                            <p className="text-sm">No backups yet</p>
                            <p className="text-xs">Create your first backup above to get started.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {backups.map((backup) => (
                                <div
                                    key={backup.id}
                                    className="flex items-center justify-between rounded-lg border px-4 py-3 transition-colors hover:bg-muted/30 group"
                                >
                                    <div className="flex-1 min-w-0 space-y-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium text-sm">{backup.name}</span>
                                            <StorageBackendBadge backend={backup.storage_backend} />
                                            <span className="text-xs text-muted-foreground">{formatBytes(backup.size_bytes)}</span>
                                        </div>
                                        {backup.description && (
                                            <p className="text-xs text-muted-foreground truncate">{backup.description}</p>
                                        )}
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <Clock className="h-3 w-3" />
                                                {formatDate(backup.created_at)}
                                            </span>
                                            <div className="flex gap-1 flex-wrap">
                                                {(backup.entities || []).slice(0, 5).map((e) => (
                                                    <Badge key={e} variant="outline" className="text-[10px] px-1.5 py-0">
                                                        {e.replace(/_/g, ' ')}
                                                    </Badge>
                                                ))}
                                                {(backup.entities || []).length > 5 && (
                                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                                        +{backup.entities.length - 5} more
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1 shrink-0 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button variant="ghost" size="sm" onClick={() => handleDownload(backup)} title="Download">
                                            <Download className="h-4 w-4" />
                                        </Button>
                                        <AuthorizationGate actions={['system_import', 'system_factory_reset']}>
                                            <Button
                                                variant="ghost" size="sm"
                                                onClick={() => { setRestoreTarget(backup); setRestoreDialogOpen(true) }}
                                                title="Restore"
                                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                            >
                                                <RotateCcw className="h-4 w-4" />
                                            </Button>
                                        </AuthorizationGate>
                                        <Button
                                            variant="ghost" size="sm"
                                            onClick={() => { setDeleteTarget(backup); setDeleteDialogOpen(true) }}
                                            title="Delete"
                                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ── Default Templates ── */}
            <AuthorizationGate actions={['system_import']}>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <PackageOpen className="h-5 w-5" />
                            Default Data Templates
                        </CardTitle>
                        <CardDescription>
                            Pre-configured data packages for bootstrapping or resetting the system.
                            Templates use upsert logic — existing records are updated, new ones are created.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {templatesLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <LoadingSpinner />
                            </div>
                        ) : !templates || templates.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2">
                                <PackageOpen className="h-10 w-10 opacity-30" />
                                <p className="text-sm">No templates available</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {templates.map((template) => (
                                    <div
                                        key={template.filename}
                                        className="flex items-center justify-between rounded-lg border px-4 py-3 transition-colors hover:bg-muted/30"
                                    >
                                        <div className="flex-1 min-w-0 space-y-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-sm">{template.name}</span>
                                                {template.tags.map((tag) => (
                                                    <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0">
                                                        {tag}
                                                    </Badge>
                                                ))}
                                                <span className="text-xs text-muted-foreground">{formatBytes(template.size_bytes)}</span>
                                            </div>
                                            <p className="text-xs text-muted-foreground">{template.description}</p>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleImportTemplate(template)}
                                            disabled={importingTemplate === template.filename}
                                            className="shrink-0 ml-4"
                                        >
                                            {importingTemplate === template.filename ? (
                                                'Importing…'
                                            ) : (
                                                <>
                                                    <Download className="h-4 w-4 mr-1.5" />
                                                    Import
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </AuthorizationGate>

            {/* ── Restore Confirmation Dialog ── */}
            <Dialog open={restoreDialogOpen} onOpenChange={(open) => {
                if (!open) {
                    setRestoreConfirmText('')
                    setRestoreTarget(null)
                    setUploadFile(null)
                }
                setRestoreDialogOpen(open)
            }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-600">
                            <ShieldAlert className="h-5 w-5" />
                            Restore from Backup
                        </DialogTitle>
                        <DialogDescription>
                            {uploadFile 
                                ? `This will factory reset categories contained in "${uploadFile.name}" and restore its data. This action cannot be undone.`
                                : `This will factory reset all entity categories included in this backup, then import the backup data. This action cannot be undone.`
                            }
                        </DialogDescription>
                    </DialogHeader>

                    {restoreTarget && (
                        <div className="space-y-4 py-2">
                            <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                                <p className="text-sm font-medium">{restoreTarget.name}</p>
                                {restoreTarget.description && (
                                    <p className="text-xs text-muted-foreground">{restoreTarget.description}</p>
                                )}
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Clock className="h-3 w-3" />
                                    {formatDate(restoreTarget.created_at)}
                                    <span>•</span>
                                    {formatBytes(restoreTarget.size_bytes)}
                                </div>
                                <div className="flex gap-1 flex-wrap">
                                    {(restoreTarget.entities || []).map((e) => (
                                        <Badge key={e} variant="outline" className="text-[10px] px-1.5 py-0">
                                            {e.replace(/_/g, ' ')}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            {restoreTarget.entities?.includes('users') && (
                                <div className="flex items-start gap-2 rounded-md bg-amber-500/10 px-3 py-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                                    <p className="text-xs text-amber-700 dark:text-amber-400">
                                        <strong>Warning:</strong> This backup includes user data. Restoring will
                                        log you out and require re-authentication.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {uploadFile && (
                        <div className="space-y-4 py-2">
                            <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm">{uploadFile.name}</span>
                                    <Badge variant="outline">Uploaded File</Badge>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <FileArchive className="h-3 w-3" />
                                    {formatBytes(uploadFile.size)}
                                </div>
                            </div>

                            <div className="flex items-start gap-2 rounded-md bg-amber-500/10 px-3 py-2">
                                <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-700 dark:text-amber-400">
                                    <strong>Warning:</strong> The system will be reset and restored using this file.
                                    If the file contains user data, you will be logged out.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="space-y-2 pt-1 px-1">
                        <Label htmlFor="restore-confirm" className="text-sm font-medium">
                            Type <span className="font-mono font-bold text-amber-600">RESTORE</span> to confirm
                        </Label>
                        <Input
                            id="restore-confirm"
                            placeholder="Type RESTORE here"
                            value={restoreConfirmText}
                            onChange={(e) => setRestoreConfirmText(e.target.value)}
                            className="font-mono"
                            autoComplete="off"
                        />
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setRestoreDialogOpen(false)
                            setRestoreConfirmText('')
                            setRestoreTarget(null)
                        }} disabled={restoreBackup.isPending}>
                            Cancel
                        </Button>
                        <Button
                            variant="default"
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={handleRestore}
                            disabled={restoreConfirmText !== 'RESTORE' || restoreBackup.isPending || isRestoringUpload}
                        >
                            {restoreBackup.isPending || isRestoringUpload ? 'Restoring…' : 'Restore Backup'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Danger Zone ── */}
            <AuthorizationGate actions={['system_factory_reset']}>
                <Card className="border-destructive/50">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-destructive">
                            <ShieldAlert className="h-5 w-5" />
                            Danger Zone
                        </CardTitle>
                        <CardDescription>
                            Selectively delete data from the system by category. This action cannot be undone.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button
                            variant="destructive"
                            onClick={() => setResetDialogOpen(true)}
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Factory Reset
                        </Button>
                    </CardContent>
                </Card>
            </AuthorizationGate>



            {/* ── Delete Confirmation Dialog ── */}
            <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
                if (!open) setDeleteTarget(null)
                setDeleteDialogOpen(open)
            }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-destructive">Delete Backup</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete "{deleteTarget?.name}"? This will remove both the metadata and the stored backup file.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null) }} disabled={deleteBackup.isPending}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={deleteBackup.isPending}>
                            {deleteBackup.isPending ? 'Deleting…' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Factory Reset Confirmation Dialog */}
            <Dialog open={resetDialogOpen} onOpenChange={(open) => {
                if (!open) {
                    setResetConfirmText('')
                    setResetCategories(new Set())
                }
                setResetDialogOpen(open)
            }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <ShieldAlert className="h-5 w-5" />
                            Factory Reset
                        </DialogTitle>
                        <DialogDescription>
                            Select which data categories to delete. Leave all unchecked to delete everything.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        {/* Category Selection */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium">Data Categories</span>
                                <button
                                    type="button"
                                    className="text-xs text-primary hover:underline cursor-pointer"
                                    onClick={() => {
                                        if (resetCategories.size === RESET_CATEGORY_OPTIONS.length) {
                                            setResetCategories(new Set())
                                        } else {
                                            setResetCategories(new Set(RESET_CATEGORY_OPTIONS.map(c => c.key)))
                                        }
                                    }}
                                >
                                    {resetCategories.size === RESET_CATEGORY_OPTIONS.length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {RESET_CATEGORY_OPTIONS.filter(c => c.key !== 'identity').map(cat => (
                                    <label
                                        key={cat.key}
                                        className="flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer hover:bg-accent transition-colors"
                                    >
                                        <Checkbox
                                            className="mt-0.5"
                                            checked={resetCategories.has(cat.key)}
                                            onCheckedChange={() => {
                                                setResetCategories(prev => {
                                                    const next = new Set(prev)
                                                    if (next.has(cat.key)) next.delete(cat.key)
                                                    else next.add(cat.key)
                                                    return next
                                                })
                                            }}
                                        />
                                        <div className="min-w-0">
                                            <span className="text-sm font-medium">{cat.label}</span>
                                            <p className="text-xs text-muted-foreground">{cat.description}</p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Identity section — special warning */}
                        <div className={cn(
                            'rounded-md border-2 p-3 transition-colors',
                            resetCategories.has('identity')
                                ? 'border-amber-500 bg-amber-500/5'
                                : 'border-dashed border-muted-foreground/25',
                        )}>
                            <label className="flex items-start gap-2 cursor-pointer">
                                <Checkbox
                                    className="mt-0.5"
                                    checked={resetCategories.has('identity')}
                                    onCheckedChange={() => {
                                        setResetCategories(prev => {
                                            const next = new Set(prev)
                                            if (next.has('identity')) next.delete('identity')
                                            else next.add('identity')
                                            return next
                                        })
                                    }}
                                />
                                <div className="min-w-0">
                                    <span className="text-sm font-medium">Users & Roles</span>
                                    <p className="text-xs text-muted-foreground">All user accounts, role definitions, and role assignments</p>
                                </div>
                            </label>
                            {resetCategories.has('identity') && (
                                <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-500/10 px-3 py-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                                    <p className="text-xs text-amber-700 dark:text-amber-400">
                                        <strong>Warning:</strong> Deleting users & roles will log you out and
                                        require you to re-authenticate. You will need to go through the initial
                                        setup flow again.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Confirmation Input */}
                        <div className="space-y-2 pt-1">
                            <Label htmlFor="reset-confirm" className="text-sm font-medium">
                                Type <span className="font-mono font-bold text-destructive">RESET</span> to confirm
                            </Label>
                            <Input
                                id="reset-confirm"
                                placeholder="Type RESET here"
                                value={resetConfirmText}
                                onChange={(e) => setResetConfirmText(e.target.value)}
                                className="font-mono"
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setResetDialogOpen(false)
                                setResetConfirmText('')
                                setResetCategories(new Set())
                            }}
                            disabled={factoryReset.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleFactoryReset}
                            disabled={resetConfirmText !== 'RESET' || factoryReset.isPending}
                        >
                            {factoryReset.isPending
                                ? 'Resetting…'
                                : resetCategories.size === 0
                                    ? 'Delete All Data'
                                    : `Delete ${resetCategories.size} ${resetCategories.size === 1 ? 'Category' : 'Categories'}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ImportResultsDialog
                open={restoreResultsOpen}
                onOpenChange={setRestoreResultsOpen}
                results={restoreResults}
                title="Restore Results"
            />
            <ImportResultsDialog
                open={templateResultsOpen}
                onOpenChange={setTemplateResultsOpen}
                results={templateResults}
                title="Template Import Results"
            />
        </div>
    )
}
